import { useSyncExternalStore } from "react";
import { parseRecent, touchRecent } from "../lib/recent.js";
import type { RecentUse } from "../lib/recent.js";

/** Stores opaque item ids and timestamps only – never titles or secrets. */
const STORAGE_KEY = "pf.recentUse";
const EMPTY: ReadonlyArray<RecentUse> = [];
const listeners = new Set<() => void>();
let cache: { readonly raw: string | null; readonly list: ReadonlyArray<RecentUse> } | undefined;

function read(): ReadonlyArray<RecentUse> {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return cache?.list ?? EMPTY;
  }
  if (cache === undefined || cache.raw !== raw) {
    cache = { raw, list: parseRecent(raw) };
  }
  return cache.list;
}

function write(list: ReadonlyArray<RecentUse>): void {
  const raw = JSON.stringify(list);
  cache = { raw, list };
  try {
    window.localStorage.setItem(STORAGE_KEY, raw);
  } catch {
    // Storage unavailable: keep the in-memory list for this tab.
  }
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}

export function markUsed(id: string): void {
  write(touchRecent(read(), id, Date.now()));
}

export function clearRecentUse(): void {
  write([]);
}

export function useRecentUse(): ReadonlyArray<RecentUse> {
  return useSyncExternalStore(subscribe, read, (): ReadonlyArray<RecentUse> => EMPTY);
}
